import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Controller } from 'react-hook-form';
import { Placement } from 'react-bootstrap/esm/Overlay';
import t from 'react-translate';
import { mergeRefs } from 'shared/react-utils';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import ValidationErrorMessage from 'shared/components/inputs/validation-error-message';
import { almostBlack, novoEdBlue } from 'athena/styles/colors';
import { danger, gray2, gray3, gray7 } from 'styles/global_defaults/colors';
import { openSans, textMediumFontSize, textMediumLineHeight } from 'styles/global_defaults/fonts';
import {
  doubleSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';


export enum AthenaTextInputType {
  NUMBER = 'number',
  SEARCH = 'search',
  TEXT = 'text',
}

type BaseAthenaTextInputProps = Omit<React.ComponentProps<'input'>, 'className'> & {
  icon?: string;
  error?: string;
  className?: string;
  label?: string;
  showLabel?: boolean;
  inputClassName?: string;
  iconPosition?: 'left' | 'right';
  popoverPlacement?: Placement;
  dataQa?: string;
  dataQaId?: string;
  validationErrorTitle?: string;
  showClearLabel?: boolean;
};

const BaseAthenaTextInput = React.forwardRef<HTMLInputElement, BaseAthenaTextInputProps>((props, ref) => {
  const {
    icon,
    error,
    onBlur,
    onFocus,
    required,
    onChange,
    className,
    placeholder,
    inputClassName,
    label,
    showLabel = true,
    value: propsValue,
    iconPosition = 'left',
    type = AthenaTextInputType.TEXT,
    popoverPlacement = 'top',
    dataQa,
    dataQaId,
    validationErrorTitle,
    showClearLabel = false,
    ...restProps
  } = props;

  const [value, setValue] = useState(propsValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setValue(propsValue);
  }, [propsValue]);

  const styles = css`
    .input-label {
      left: 0;
      top: -${standardSpacing}px;
    }

    .athena-input-container {
      .icon-container {
        ${iconPosition}: 0;
        width: ${doubleSpacing}px;
      }

      .athena-input {
        height: ${doubleSpacing}px;
        color:  ${value ? almostBlack : gray2};
        border: 1px solid ${error ? danger : gray3};

        // Defining font styles explicitely and not using 'text-medium' class in
        // input because of a specificity issue.
        font-family: ${openSans};
        font-size: ${textMediumFontSize}px !important;
        line-height: ${textMediumLineHeight}px !important;

        ${required && css`
          padding-right: ${largeSpacing}px;
        `};
  
        ${icon && css`
          padding-${iconPosition}: ${doubleSpacing}px !important;
        `};

        &:focus {
          outline: 1px solid ${error ? danger : novoEdBlue};
        }
      }

      .required-indicator {
        top: 0;
        right: 0;
        width: ${largeSpacing}px;
      }

      .clear-button {
        top: 0;
        right: ${quarterSpacing}px;
        border: none;
        background: none;
      }
    }
  `;

  const handleChange = (e) => {
    setValue(e?.target?.value ?? '');

    onChange?.(e);
  };

  const handleFocus = (e) => {
    setIsFocused(true);

    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    onBlur?.(e);
  };

  return (
    <div css={styles} className={className}>
      {showLabel && (
        <div className='input-label text-small color-gray-1 font-weight-bold mb-1'>
          {label || placeholder}
        </div>
      )}
      <div className='athena-input-container position-relative'>
        {!!icon && (
          <div className='icon-container position-absolute d-flex align-items-center justify-content-center h-100'>
            <NvIcon icon={icon} size='small' />
          </div>
        )}
        <NvPopover
          className='nv-text-input'
          placement={popoverPlacement}
          show={!!error && isFocused}
          content={
            (
              <ValidationErrorMessage
                text={error}
                title={validationErrorTitle || t.VALIDATION.ERROR()}
              />
            )
          }
        >
          <input
            ref={ref}
            type={type}
            value={value}
            onBlur={handleBlur}
            required={required}
            onFocus={handleFocus}
            onChange={handleChange}
            placeholder={placeholder}
            className={`athena-input w-100 p-2 border-radius-1 ellipsis ${inputClassName ?? ''}`}
            data-qa={dataQa}
            data-qa-id={dataQaId}
            aria-label={label || placeholder}
            {...restProps}
          />
        </NvPopover>
        {required && (
          <div className='required-indicator position-absolute h-100 d-flex align-items-center justify-content-center text-medium gray-2'>
            *
          </div>
        )}
        {type === AthenaTextInputType.SEARCH && value && (
          <button
            className='clear-button position-absolute d-flex align-items-center justify-content-center h-100'
            onClick={handleChange}
            type='button'
            aria-label={t.MENTORING_PROGRAMS.ARIA_LABEL.CLEAR_SEARCH()}
          >
            {showClearLabel ? (
              <span className='text-small gray-2'>{t.SHARED.CLEAR()}</span>
            ) : (
              <NvIcon icon='close' size='xss-smallest' color={gray2} />
            )}
          </button>
        )}
      </div>
    </div>
  );
});

type Props = BaseAthenaTextInputProps & {
  withForm?: boolean;
};

const AthenaTextInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    name,
    value,
    error,
    onBlur,
    onChange,
    withForm,
    type = AthenaTextInputType.TEXT,
    ...restProps
  } = props;

  return withForm ? (
    <Controller
      name={name}
      render={({ field, fieldState }) => {
        const handleBlur = (e) => {
          onBlur?.(e);
          field.onBlur();
        };

        const handleChange = (e) => {
          onChange?.(e);
          field.onChange(e);
        };

        return (
          <BaseAthenaTextInput
            name={field.name}
            onBlur={handleBlur}
            onChange={handleChange}
            value={value ?? field.value}
            error={error ?? fieldState.error?.message}
            {...restProps}
            ref={mergeRefs(ref, field.ref)}
            type={type}
          />
        );
      }}
    />
  ) : (
    <BaseAthenaTextInput
      name={name}
      value={value}
      error={error}
      onBlur={onBlur}
      onChange={onChange}
      {...restProps}
      ref={ref}
      type={type}
    />
  );
});

export default AthenaTextInput;
